import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DialogShell } from '@timely/ui';
import { CHEQUE_STATUS_CHOICES, ChequeStatus } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { useFormatters } from '../book/useFormatters';
import { FormNotice } from '../screens/AuthLayout';
import {
    ChequeError, customerById, newChequeId, saveCheque, searchCustomers,
    type ChequeDraft, type ChequeRow, type CustomerOption,
} from './useCheques';

/**
 * Recording a cheque, or correcting one.
 *
 * Three things this is careful about, each of which cost the reference
 * implementation a real bug:
 *
 *   **The id is made before the first attempt** and reused on every retry, so
 *   a save that times out and is pressed again lands on the same row instead
 *   of creating a second cheque for the same money.
 *
 *   **A failed save keeps what was typed.** The dialog stays open with every
 *   field as it was; the only thing that changes is that it now says what went
 *   wrong. Closing on failure and losing a cheque somebody has in their hand
 *   is how people stop trusting a form.
 *
 *   **The date is a calendar day in the company's timezone.** It is seeded from
 *   `date.todayValue()`, not from the browser's UTC day, because a cheque
 *   recorded at 9am in Mumbai came back dated the day before.
 */
export function ChequeDialog({
    cheque, onClose, onSaved,
}: {
    cheque: ChequeRow | null;
    onClose: () => void;
    onSaved: (message: string) => void | Promise<void>;
}) {
    const context = useActiveWorkspace();
    const { workspace, membership } = context;
    const { money, date } = useFormatters();

    // Made once, at the moment the dialog opens, and kept for the life of it.
    const idRef = useRef(cheque?.id ?? newChequeId());

    const [draft, setDraft] = useState<ChequeDraft>(() => ({
        id: idRef.current,
        customerId: cheque?.customerId ?? null,
        customerName: cheque?.customerName ?? '',
        chequeNumber: cheque?.chequeNumber ?? '',
        bankName: cheque?.bankName ?? '',
        chequeDate: cheque?.chequeDate ? date.inputValue(cheque.chequeDate) : date.todayValue(),
        amount: cheque ? String(cheque.amount) : '',
        status: cheque?.status ?? ChequeStatus.Pending,
        receivedDate: cheque?.receivedDate ? date.inputValue(cheque.receivedDate) : date.todayValue(),
        remarks: cheque?.remarks ?? '',
        ownerMemberId: cheque?.ownerMemberId ?? (membership.memberId as string),
    }));

    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);

    const set = (changes: Partial<ChequeDraft>) => setDraft(d => ({ ...d, ...changes }));

    const amount = Math.abs(Number(draft.amount.replace(/[^0-9.-]/g, '')) || 0);
    const problems = useMemo(() => {
        const list: string[] = [];
        if (!draft.customerName.trim()) list.push('Say whose cheque this is.');
        if (amount <= 0) list.push('A cheque has an amount.');
        if (!draft.chequeDate) list.push('A cheque has a date on it.');
        return list;
    }, [draft.customerName, draft.chequeDate, amount]);

    const save = async () => {
        setTouched(true);
        if (problems.length > 0 || saving) return;

        setSaving(true);
        setProblem(null);
        try {
            await saveCheque(workspace.id, draft, membership.memberId as string);
            await onSaved(cheque ? 'That cheque has been updated.' : 'That cheque has been recorded.');
        } catch (failed) {
            // Everything typed stays exactly where it is.
            setProblem(failed instanceof ChequeError
                ? failed.message
                : 'That did not save. Try again in a moment.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogShell
            title={cheque ? 'Correct this cheque' : 'Record a cheque'}
            subtitle={cheque
                ? 'Change what was written down. Where the cheque stands is changed from the register.'
                : 'A cheque somebody has handed you, with the date written on it.'}
            size="md"
            onClose={onClose}
            closeOnEscape={!saving}
            onSubmit={e => { e.preventDefault(); void save(); }}
            footerNote={amount > 0 ? money.exact(amount) : undefined}
            footer={
                <>
                    <Button type="button" variant="quiet" disabled={saving} onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? 'Saving…' : cheque ? 'Save changes' : 'Record it'}
                    </Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}
            {touched && problems.length > 0 && (
                <div className="mb-4"><FormNotice tone="error">{problems.join(' ')}</FormNotice></div>
            )}

            <CustomerPicker
                workspaceId={workspace.id}
                customerId={draft.customerId}
                customerName={draft.customerName}
                onPick={(option: CustomerOption | null, typed: string) =>
                    set({ customerId: option?.id ?? null, customerName: option?.company ?? typed })}
            />

            <div className="grid gap-x-4 sm:grid-cols-2 mt-4">
                <Field
                    id="cheque-number" label="Cheque number" value={draft.chequeNumber}
                    onChange={v => set({ chequeNumber: v })} inputMode="numeric"
                />
                <Field
                    id="cheque-bank" label="Bank" value={draft.bankName}
                    onChange={v => set({ bankName: v })}
                />
                <Field
                    id="cheque-amount" label="Amount" value={draft.amount} required
                    onChange={v => set({ amount: v })} inputMode="decimal"
                />
                <Field
                    id="cheque-date" label="Date on the cheque" value={draft.chequeDate} required
                    type="date" onChange={v => set({ chequeDate: v })}
                    hint={`In ${date.zoneLabel}`}
                />
                <Field
                    id="cheque-received" label="Received on" value={draft.receivedDate}
                    type="date" onChange={v => set({ receivedDate: v })}
                />
                <div className="mb-3.5">
                    <label htmlFor="cheque-status" className="block text-[13px] font-semibold text-label mb-1">
                        Where it stands
                    </label>
                    <select
                        id="cheque-status"
                        value={draft.status}
                        onChange={e => set({ status: e.target.value as ChequeStatus })}
                        className="w-full h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                    >
                        {CHEQUE_STATUS_CHOICES.map(choice => (
                            <option key={choice.value} value={choice.value}>{choice.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mb-1">
                <label htmlFor="cheque-remarks" className="block text-[13px] font-semibold text-label mb-1">
                    Note
                </label>
                <textarea
                    id="cheque-remarks"
                    rows={2}
                    value={draft.remarks}
                    onChange={e => set({ remarks: e.target.value })}
                    className="w-full px-3 py-2 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent resize-none"
                />
            </div>
        </DialogShell>
    );
}

/* -------------------------------------------------------------------------- */

function Field({
    id, label, value, onChange, type = 'text', required, hint, inputMode,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    type?: string;
    required?: boolean;
    hint?: string;
    inputMode?: 'numeric' | 'decimal';
}) {
    return (
        <div className="mb-3.5">
            <label htmlFor={id} className="block text-[13px] font-semibold text-label mb-1">
                {label}{required && <span className="text-dang"> *</span>}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                inputMode={inputMode}
                onChange={e => onChange(e.target.value)}
                className="w-full h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
            />
            {hint && <div className="text-[12px] text-label-3 mt-1">{hint}</div>}
        </div>
    );
}

/**
 * Which account this cheque belongs to.
 *
 * A search box, not a select with four thousand options in it — that is
 * unusable on a phone and slow everywhere. The search runs through `book_page`,
 * so it only ever returns accounts this person may open: an account outside
 * their scope cannot be found by typing its exact name, and cannot be attached
 * by any means this screen offers. Supplying an id by hand would not help
 * either — the insert policy asks the same question again.
 *
 * A cheque may be left unattached. Somebody is holding a cheque from a walk-in
 * customer who is not in the book yet, and refusing to record it would mean the
 * money is not written down anywhere.
 */
function CustomerPicker({
    workspaceId, customerId, customerName, onPick,
}: {
    workspaceId: string;
    customerId: string | null;
    customerName: string;
    onPick: (option: CustomerOption | null, typed: string) => void;
}) {
    const [query, setQuery] = useState(customerName);
    const [options, setOptions] = useState<CustomerOption[]>([]);
    const [open, setOpen] = useState(false);
    const [looking, setLooking] = useState(false);
    const latest = useRef(0);

    // A cheque being corrected already names an account; confirm it still
    // resolves, so an account somebody lost access to shows as unlinked rather
    // than as a name with no record behind it.
    useEffect(() => {
        if (!customerId) return;
        void (async () => {
            const found = await customerById(workspaceId, customerId);
            if (!found) onPick(null, customerName);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        if (!open || query.trim().length < 2) { setOptions([]); return; }
        const ticket = ++latest.current;
        setLooking(true);
        const timer = setTimeout(() => {
            void (async () => {
                const found = await searchCustomers(workspaceId, query.trim());
                // An answer that arrives after somebody typed more is about a
                // question they are no longer asking.
                if (ticket !== latest.current) return;
                setOptions(found);
                setLooking(false);
            })();
        }, 180);
        return () => clearTimeout(timer);
    }, [query, open, workspaceId]);

    return (
        <div className="relative">
            <label htmlFor="cheque-customer" className="block text-[13px] font-semibold text-label mb-1">
                Whose cheque is it? <span className="text-dang">*</span>
            </label>
            <input
                id="cheque-customer"
                type="text"
                role="combobox"
                aria-expanded={open && options.length > 0}
                aria-controls="cheque-customer-options"
                aria-autocomplete="list"
                autoComplete="off"
                value={query}
                placeholder="Start typing a customer’s name"
                onChange={e => { setQuery(e.target.value); setOpen(true); onPick(null, e.target.value); }}
                onFocus={() => setOpen(true)}
                onBlur={() => { window.setTimeout(() => setOpen(false), 150); }}
                className="w-full h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
            />

            <div className="text-[12px] text-label-3 mt-1">
                {customerId
                    ? 'Linked to this account — its history will show what happens to this cheque.'
                    : 'Not linked to an account yet. You can still record the cheque.'}
            </div>

            {open && query.trim().length >= 2 && (
                <ul
                    id="cheque-customer-options"
                    role="listbox"
                    className="absolute z-10 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-[10px] border border-separator-strong bg-card shadow-lg"
                >
                    {looking && (
                        <li className="px-3 py-2 text-[13px] text-label-3">Looking…</li>
                    )}
                    {!looking && options.length === 0 && (
                        <li className="px-3 py-2 text-[13px] text-label-3">
                            No account of yours matches that. The cheque can be recorded without one.
                        </li>
                    )}
                    {options.map(option => (
                        <li key={option.id} role="option" aria-selected={option.id === customerId}>
                            <button
                                type="button"
                                className="w-full text-left px-3 py-2 min-h-[40px] hover:bg-accent-soft focus:bg-accent-soft focus:outline-none"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => { onPick(option, option.company); setQuery(option.company); setOpen(false); }}
                            >
                                <span className="text-[13.5px] text-label font-semibold">{option.company}</span>
                                {option.sourceKey && (
                                    <span className="text-[12px] text-label-3 num ml-2">{option.sourceKey}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
